import FileUploadInput from "components/DynamicForm/components/FileUploadInput";
import React, { useEffect, useState } from "react";
import { Animated } from "react-animated-css";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import styles from "./style.module.scss";
import {
  Alert,
  Button, Col, Container, Modal, ModalBody, ModalHeader, Row
} from "reactstrap";
import { errorHandler } from "utils/Api";
import { showAlert } from "utils/Alert";
import { Predictions } from '@aws-amplify/predictions';
import { mergeWith, isNull } from "lodash";

import syncWarehouseForm from 'formConfigs/syncWarehouseData';
import sampleData from "./samplesJson/prediction-sample-2.json"
import boxesTableMapping from "./boxesTableMapping";
import { DYNAMIC_DATA_SPLITTER, DYNAMIC_DATA_FORMAT_INDEX, DYNAMIC_DATA_VALUE_INDEX, PO_BOX_PREFIX, PACKAGE_NOTIFICATIONS } from "utils/Constants";
import LoadingScreen from "components/LoadingScreen";
import { getPackageTrackingNumber, savePackage } from "api/PackagesApi";
import { getPoBoxByNumber } from "api/POBoxApi";
import DynamicForm from "components/DynamicForm";
import { post as postAPICall } from 'aws-amplify/api';

const PDF_MIME_TYPE = "application/pdf";
const JPG_MIME_TYPE = "image/jpg";

const AddWarehouseModal = (props) => {
  let navigate = useNavigate();
  const { isOpen, toggle } = props;
  const [isFromPdf, setIsFromPdf] = useState(false);
  const [IsFromWarehouseApi, setIsFromWarehouseApi] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBoxSynced, setIsBoxSynced] = useState(false);
  const [boxSyncedData, setBoxSyncedData] = useState({});

  const [files, setFiles] = useState([]);
  const [pdfImage, setPdfImage] = useState();

  useEffect(() => {
    if (!isOpen) {
      setIsFromPdf(false);
      setIsFromWarehouseApi(false);
      setFiles([]);
      setPdfImage(null);
      setIsLoading(false);
      setIsBoxSynced(false);
      setBoxSyncedData({})
    }
  }, [isOpen]);

  const getWarehouseFormConfig = () => {
    return syncWarehouseForm;
  }

  const onUpdateFiles = (files) => {
    if (files.length) {
      const file = files[0];
      if (file.type === PDF_MIME_TYPE) {
        setFiles(files);
      } else if (file.type === JPG_MIME_TYPE) {
        setPdfImage(file);
        setFiles([...files]);
      }
    } else {
      setFiles([]);
      setPdfImage(null);
    }
  }

  const importPDFImage = () => {
    setIsLoading(true);

    processPDF().then(box => {
      navigate(`/admin/package/edit/${box.id}`);
    }).catch(err => {
      showAlert("danger", `No fue posible procesar el PDF seleccionado, por favor crearlo manualmente`);
      errorHandler(err, "Error al procesar la imagen");
    }).finally(() => {
      setIsLoading(false);
    });
  }

  const processPDF = async () => {
    const { data: file } = pdfImage;
    const response = await Predictions.identify({
      text: {
        source: {
          file
        },
        format: "ALL",
      }
    });
    // return await buildPackageDataFromJson(sampleData);
    return await buildPackageDataFromJson(response);
  }

  const groupBoxesData = (tableRowValues, chunkSize = 3) => {
    const res = [];
    for (let i = 0; i < tableRowValues.length; i += chunkSize) {
      const chunk = tableRowValues.slice(i, i + chunkSize);
      if (res.length === 0 || chunk.length === chunkSize) {
        res.push(chunk);
      }
    }
    return res;
  }

  const getTotalOfProperty = (boxes, property) => {
    let format = "";
    if (boxes.length > 0) {
      const value = boxes[0][property]?.split(DYNAMIC_DATA_SPLITTER);
      format = value[DYNAMIC_DATA_FORMAT_INDEX] || format;
    }
    const total = boxes.reduce((a, v) => {
      const value = boxes[0][property]?.split(DYNAMIC_DATA_SPLITTER);
      return a + new Number(value[DYNAMIC_DATA_VALUE_INDEX]);
    }, new Number(0));
    return `${total}${DYNAMIC_DATA_SPLITTER}${format}`;
  }

  const getBoxMeasure = (boxes) => {
    if (boxes.length === 1) {
      return boxes[0].measure;
    }
    return "";
  }

  const getBoxesData = (tablesData) => {
    const tableRowValues = tablesData
      .filter(t => t.size.columns === 7)// get only the tables that match with the boxes table columns 
      .map(t => {// Remove the heading table rows 
        const filterTable = t.table.filter(r => {
          const firstColumn = r[0]?.text;
          return firstColumn !== "Pcs Package" && firstColumn !== "Location" && firstColumn !== "Quantity";
        });
        return {
          ...t,
          table: filterTable
        }
      }).flatMap(t => [...t.table]);// merge all the tables on a single table

    const boxesData = groupBoxesData(tableRowValues);

    const boxes = boxesData.map(b => {
      return Object.keys(boxesTableMapping)
        .map(property => {
          const propertyMap = boxesTableMapping[property];
          let value = b[propertyMap.row][propertyMap.column]?.text;

          if (propertyMap.separator) {
            const values = value.split(propertyMap.splitBy);
            if (propertyMap.isMeasure) {
              values[1] = propertyMap.format;
            }
            value = values.join(propertyMap.separator);
          }

          return {
            [property]: value
          };
        }).reduce((a, b) => { return { ...a, ...b } }, {});
    })

    const totalVWeightFt3 = getTotalOfProperty(boxes, "vWeightFt3");
    const totalWeightKg = getTotalOfProperty(boxes, "weightKg");
    const totalWeightLbs = getTotalOfProperty(boxes, "weightLbs");
    const totalVWeightVlb = getTotalOfProperty(boxes, "vWeightVlb");
    const measures = getBoxMeasure(boxes);

    return {
      totalVWeightFt3,
      totalWeightKg,
      totalWeightLbs,
      totalVWeightVlb,
      measures,
      boxes
    }
  }

  const buildPackageDataFromJson = async (data) => {
    const keyValues = data.text.keyValues;
    const tablesData = data.text.tables;
    const boxesData = getBoxesData(tablesData);

    const client = keyValues.find(kv => kv.key.includes("Consignee Information"));
    const provedor = keyValues.find(kv => kv.key.includes("Supplier Name"));
    const carrierName = keyValues.find(kv => kv.key.includes("Carrier Name"));
    const warehouseNumber = keyValues.find(kv => kv.key.includes("Receipt Number"));
    const trackingNumberEntry = keyValues.find(kv => kv.key.includes("Tracking Number"));
    const trackingNumber = (trackingNumberEntry?.value?.text || "").toUpperCase().replace(/ /g, '');

    let box = {
      client: client?.value?.text || "",
      provedor: provedor?.value?.text || "",
      origin: "US",
      destino: "CR",
      warehouseNumber: warehouseNumber?.value?.text || "",
      measures: boxesData.measures,
      weigth: boxesData.totalWeightLbs,
      vweigth: boxesData.totalVWeightFt3,
      tracking_num: trackingNumber,
      courier: carrierName?.value?.text || "",
      boxes: boxesData.boxes,
      data: JSON.stringify(data),
      received: true,
      notificationType: PACKAGE_NOTIFICATIONS.PACKAGE_RECEIVED
    };

    if (box.tracking_num) {
      const currentPackage = await getPackageTrackingNumber(box.tracking_num);
      box = mergeWith({}, box, currentPackage, (o, s) => isNull(s) ? o : s);
      box = {
        ...box,
        notificationNeeded: true
      }
    }

    if (box.client) {
      const poBoxNumber = box.client.replace(/\s/g, "").replace(PO_BOX_PREFIX, "");
      const poBox = await getPoBoxByNumber(poBoxNumber);
      if (poBox) {
        box = {
          ...box,
          poBoxBoxesId: poBox.id,
          notificationNeeded: true,
        }
      }
    }

    return await savePackage(box);
  }

  const getModalSizing = () => {
    return isFromPdf || IsFromWarehouseApi ? "lg" : "md";
  }

  const getFileUploadCustomProps = () => {
    return {
      "imagePreviewMaxHeight": 500,
      "pdfConvertType": 'image/jpg',
    }
  }

  const syncWarehouseTrackingNumber = async (formData) => {
    try {
      setIsLoading(true);
      const postPayload = {
        apiName: "unboxing",
        path: "/syncWarehouseData",
        options: {
          body: formData
        }

      };
      const restOperation = await postAPICall(postPayload).response;
      const response = await restOperation.body.json();
      setBoxSyncedData(response.box)
      setIsBoxSynced(true)
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setIsBoxSynced(true)
      setBoxSyncedData({})
    }
  }

  return isOpen && createPortal(
    <>
      <Modal
        className={styles.modal}
        size={getModalSizing()}
        toggle={toggle}
        isOpen={isOpen}
      >
        <ModalHeader toggle={toggle}>
          Agregar Warehouse
        </ModalHeader>
        <ModalBody>
          <p>Usted puede agregar un Warehouse ya sea de forma manual o atravez del PDF enviado por bodega,
            por favor seleccione como desea crear el Warehouse
          </p>
          <div className={styles.modalActionContent}>
            <Animated
              animationIn="fadeInDown"
              animationOut="fadeOutDown"
              animationOutDelay={500}
              isVisible={!isFromPdf && !IsFromWarehouseApi}
              animateOnMount={false}
            >
              {(!isFromPdf && !IsFromWarehouseApi) && (
                <Row className="justify-content-around">
                  <Col sm="auto">
                    <Button
                      className="float-end"
                      color="success"
                      onClick={(e) => navigate("/admin/package/add")}
                    > Manualmente</Button>
                  </Col>
                  <Col sm="auto">
                    {/* <Button
                      className="float-end"
                      color="success"
                      onClick={(e) => setIsFromPdf(true)}
                    > Desde PDF
                    </Button> */}
                    <Button
                      className="float-end"
                      color="success"
                      onClick={(e) => setIsFromWarehouseApi(true)}
                    > Importar de Bodega Miami
                    </Button>
                  </Col>
                </Row>
              )}
            </Animated>
            <Animated
              animationIn="fadeInDown"
              animationOut="fadeOutDown"
              animationInDelay={500}
              isVisible={IsFromWarehouseApi}
              animateOnMount={false}
            >
              {IsFromWarehouseApi && (
                <>
                  <Alert color="warning">
                    Por favor ingrese el numero de tracking del package que se desea sincronizar con los datos de la bodega de Miami
                  </Alert>
                  <DynamicForm
                    id="syncWareHouse"
                    formConfig={getWarehouseFormConfig()}
                    onSubmit={syncWarehouseTrackingNumber}
                    isLoading={isLoading}>
                    <Container>

                      {!isBoxSynced && !boxSyncedData?.id && (
                        <Row>
                          <Col>
                            <Button
                              className="float-end"
                              type="submit"
                              color="success">
                              Sincronizar
                            </Button>
                          </Col>
                        </Row>
                      )
                      }
                    </Container>
                  </DynamicForm>

                  {isBoxSynced && (
                    <>
                      <Alert color={boxSyncedData && boxSyncedData.id ? "success" : "danger"}>
                        {boxSyncedData && boxSyncedData.id ?
                          "La informacion del paquete fue sincronizada en base a la information disponible en el warehouse en Miami"
                          :
                          "La informacion del paquete no esta disponible en este momento en el Warehouse de Miami"
                        }
                      </Alert>
                      {isBoxSynced && boxSyncedData && boxSyncedData.id && (
                        <Button
                          className="float-end"
                          color="success"
                          onClick={(e) => navigate(`/admin/package/edit/${boxSyncedData.id}`)}
                        > Ver Informacion de Paquete
                        </Button>
                      )}
                    </>
                  )}

                </>
              )}
            </Animated>
            <Animated
              animationIn="fadeInDown"
              animationOut="fadeOutDown"
              animationInDelay={500}
              isVisible={isFromPdf}
              animateOnMount={false}
            >
              {isFromPdf && (
                <>
                  <Alert color="warning">
                    Cada carga de PDF tiene un costo asociado, por favor solo cargue el PDF del Warehouse una unica vez.
                    El PDF tiene que cumplir con los siguientes criterios para que la lectura se realize correctamente
                    <ul>
                      <li>
                        Cada pagina del PDF tiene que estar en formato vertical
                      </li>
                      <li>
                        De preferencia usar el archivo PDF original en lugar de una version escaneada
                      </li>
                    </ul>
                  </Alert>
                  <FileUploadInput
                    customProps={getFileUploadCustomProps()}
                    files={files}
                    allowMultiple={false}
                    onUpdateFiles={onUpdateFiles}
                    name="files"
                    convertPdfToImage={true}
                    label="Arrastre el PDF de la Bodega en este area"
                  />
                  {pdfImage && (
                    <Row className="justify-content-around">
                      <Col sm="auto">
                        <Button
                          className="float-end"
                          color="success"
                          onClick={(e) => importPDFImage()}
                        > Importar informacion
                        </Button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Animated>
          </div>
        </ModalBody>
      </Modal>
      <LoadingScreen isLoading={isLoading} />
    </>
    , document.body);

}

export default AddWarehouseModal;